// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.
//
// This tab is a terminal. Every `ink-*` here is deliberate (CLAUDE.md's escape
// hatch for surfaces that must read as dark) and none of it is `gray-*` by
// accident, so the toolbar controls and the log pane stay hand-written:
// `Button`, `Input` and `Card` are all light-surface primitives and there is no
// ink variant of any of them. See the report for the missing-primitive note.

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import type { Instance } from '../../api/client'
import { Badge } from '../../components/ui'

export const SCROLL_BOTTOM_TOLERANCE = 60

export function LogsTab({ instance }: { instance: Instance }) {
  const [logs, setLogs] = useState<string[]>([])
  const [connected, setConnected] = useState(false)
  const [filter, setFilter] = useState('')
  const [atBottom, setAtBottom] = useState(true)
  const [copied, setCopied] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const esRef = useRef<EventSource | null>(null)
  const autoScrollRef = useRef(true)
  const mountedRef = useRef(true)
  const { t } = useTranslation()

  useEffect(() => {
    mountedRef.current = true

    const connect = () => {
      if (!mountedRef.current) return
      const token = useAuthStore.getState().accessToken ?? ''
      const url = `/api/v1/instances/${instance.id}/logs?tail=200&follow=true&token=${encodeURIComponent(token)}`
      const es = new EventSource(url)
      esRef.current = es

      es.onopen = () => {
        if (mountedRef.current) setConnected(true)
      }
      es.onmessage = (e) => {
        if (!mountedRef.current) return
        setLogs((prev) => [...prev.slice(-2000), e.data])
        if (autoScrollRef.current) {
          requestAnimationFrame(() => {
            const el = containerRef.current
            if (el) el.scrollTop = el.scrollHeight
          })
        }
      }
      es.onerror = () => {
        if (!mountedRef.current) return
        setConnected(false)
        es.close()
        setTimeout(connect, 3000)
      }
    }

    connect()

    return () => {
      mountedRef.current = false
      esRef.current?.close()
    }
  }, [instance.id])

  const handleScroll = () => {
    const el = containerRef.current
    if (!el) return
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_TOLERANCE
    autoScrollRef.current = isAtBottom
    setAtBottom(isAtBottom)
  }

  const scrollToBottom = () => {
    autoScrollRef.current = true
    setAtBottom(true)
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }

  const filtered = filter ? logs.filter((l) => l.toLowerCase().includes(filter.toLowerCase())) : logs

  const handleCopy = () => {
    if (filtered.length === 0) return
    navigator.clipboard.writeText(filtered.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleExport = () => {
    if (filtered.length === 0) return
    const blob = new Blob([filtered.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19)
    a.href = url
    a.download = `${instance.subdomain}-${stamp}.log`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getLineClass = (line: string) => {
    const lower = line.toLowerCase()
    if (/\b(error|critical|traceback)\b/.test(lower)) return 'text-red-400'
    if (/\b(warning|warn)\b/.test(lower)) return 'text-yellow-400'
    return 'text-gray-300'
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('detail.logs.filter')}
          className="flex-1 px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-200 placeholder-ink-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <Badge tone={connected ? 'success' : 'neutral'} className="whitespace-nowrap">
          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          {connected ? t('detail.logs.live') : t('detail.logs.disconnected')}
        </Badge>
        <button
          onClick={handleCopy}
          disabled={filtered.length === 0}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded transition-colors whitespace-nowrap"
        >
          {copied ? t('common.copiedAll') : t('common.copyAll')}
        </button>
        <button
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="text-xs font-medium text-ink-300 hover:text-ink-50 bg-ink-800 hover:bg-ink-700 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded transition-colors whitespace-nowrap"
        >
          {t('detail.logs.export')}
        </button>
        <button
          onClick={() => setLogs([])}
          className="text-xs text-ink-400 hover:text-ink-200 px-2 py-1 rounded hover:bg-ink-700 transition-colors whitespace-nowrap"
        >
          {t('detail.logs.clear')}
        </button>
      </div>
      <div className="relative">
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="bg-ink-950 border border-ink-800 rounded-xl p-4 h-[calc(100vh-22rem)] min-h-80 overflow-auto font-mono text-xs leading-5"
        >
          {filtered.length === 0 ? (
            <span className="text-gray-500">{connected ? t('detail.logs.waiting') : t('detail.logs.notConnected')}</span>
          ) : (
            filtered.map((line, i) => (
              <div key={i} className={`${getLineClass(line)} whitespace-pre-wrap break-all py-px`}>
                {line}
              </div>
            ))
          )}
        </div>
        {!atBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 bg-ink-700 hover:bg-ink-600 text-ink-200 text-xs px-3 py-1.5 rounded-full shadow-lg transition-colors"
          >
            ↓ {t('detail.logs.scrollToBottom')}
          </button>
        )}
      </div>
    </div>
  )
}
